import type { CSSProperties, ReactNode } from "react";
import { createContext, useContext, useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Checkbox,
  DatePicker,
  ErrorSummary,
  PortalPanel,
  PortalPanelBody,
  QuestionRow,
  RadioGroup,
  Select,
  Stack,
  TextField,
  tokens,
} from "../../design-system";
import {
  checkYearKey,
  checklistOtherTextKey,
  checklistTickedKey,
  createRepeatItemKey,
  detailTextKey,
  isFieldAnswerKey,
  pickedOnFileItemFor,
  readCheckYear,
  readLimitsBlocks,
  readRepeatItems,
  readString,
  readStringArray,
  repeatAnswerKey,
  repeatItemsKey,
  withPickedOnFileItem,
  yesNoDetailAnswerKey,
  type ExposureAnswers,
  type LimitsBlockAnswer,
  type RepeatItemAnswer,
} from "./exposure-answers";
import { exposureContent } from "./exposure-content";
import { getOnFileFieldSpec } from "./on-file-fields";
import type { SectionOnFileSpec } from "./on-file-fields";
import {
  excludedEpicRecordIds,
  findOnFileScreen,
  onFileAttributeText,
  soldEpicRecordIds,
  useFailureFocusClaim,
  type OnFileRenderContext,
} from "./on-file-render";
import { pickedRecord } from "./additional-details";
import { OnFilePanelBody, OnFilePanelTrigger } from "./OnFilePanel";
import { OnFilePicker } from "./OnFilePicker";
import { PICKER_NEW_OPTION_VALUE, noneLeftLabelFor } from "./exposure-schema";
import type {
  ChecklistItemSpec,
  ExposureQuestion,
  ExposureSectionSchema,
  FieldOption,
  RepeatBlockSpec,
  RepeatFieldSpec,
} from "./exposure-schema";
import { detailsShowRuleRow, isFieldOnScreen, soldSourceRows, type DetailsPageRow, type ExposureErrorSummaryItem } from "./exposure-validation";
import type { OnFileScreenItem } from "../../state/on-file-port";
import type { SectionId } from "../../routing/route-table";

export interface ExposureQuestionsEngineProps {
  readonly schema: ExposureSectionSchema;
  readonly answers: ExposureAnswers;
  readonly onChange: (key: string, value: unknown) => void;
  /** Populated only once Continue has been attempted for this section instance. */
  readonly errors: readonly ExposureErrorSummaryItem[];
  readonly disabled?: boolean;
  /** This section instance's own on-file read; every data-on-file question reads through it. */
  readonly onFile?: OnFileRenderContext;
}

const sp = tokens.spacing;
const ink = tokens.colour.neutral.ink;

/** H2 04-05-GuidedExposures `fieldsetStyle`, `legendStyle` and the repeat row styles. */
const FIELDSET_STYLE: CSSProperties = {
  border: "none",
  margin: 0,
  padding: 0,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: `${sp.sm}px`,
};
const LEGEND_STYLE: CSSProperties = {
  padding: 0,
  fontSize: 17,
  fontWeight: tokens.typography.weights.bold,
  color: ink,
  marginBottom: `${sp.xs}px`,
};
const LEGEND_HIDDEN_STYLE: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};
const ROW_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: `${sp.xs}px`,
  minWidth: 0,
  padding: `${sp.sm}px 0`,
};
const ROW_HEAD_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: `${sp.sm}px`,
};
const ROW_TITLE_STYLE: CSSProperties = { fontSize: 17, fontWeight: tokens.typography.weights.bold, color: ink };
const FIELDS_ROW_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: `${sp.sm}px`,
  minWidth: 0,
  maxWidth: 640,
};
const MUTED_NOTE_STYLE: CSSProperties = { fontSize: 15, lineHeight: 1.5, color: tokens.colour.neutral.inkMuted };

/** H2 `wrapStyle`: a wide field takes 640px, any other 360px. */
function fieldWrapStyle(wide: boolean | undefined): CSSProperties {
  return { width: "100%", maxWidth: wide === true ? 640 : 360 };
}

/** H2 numbers each question within its section ("1. Has the nature…"); a follow-up takes its
 * parent's number with a letter ("1a. Tell us what has changed"). ADR-20261102-h2fd. */
const QuestionNumberContext = createContext<string | number | undefined>(undefined);

function useNumbered(): (text: string, letter?: string) => string {
  const number = useContext(QuestionNumberContext);
  return (text, letter = "") => (number === undefined ? text : `${number}${letter}. ${text}`);
}

/** H2 keeps a question's last word and its help button together on one line, with no space
 * between them, so the button never wraps on its own. */
function withTrigger(text: string, trigger: ReactNode): ReactNode {
  const match = /^(.*\s)(\S+)$/.exec(text);
  const lead = match?.[1] ?? "";
  const last = match?.[2] ?? text;
  return (
    <>
      {lead}
      <span style={{ whiteSpace: "nowrap" }}>
        {last}
        {trigger}
      </span>
    </>
  );
}

function errorFor(errors: readonly ExposureErrorSummaryItem[], anchorId: string): string | undefined {
  const error = errors.find((candidate) => candidate.anchorId === anchorId);
  // H2: the field shows its own short message; the summary names the question.
  return error === undefined ? undefined : (error.fieldText ?? error.text);
}

const YES_NO_OPTIONS: readonly FieldOption[] = [
  { value: "yes", label: exposureContent.yesLabel },
  { value: "no", label: exposureContent.noLabel },
];

/** A question's fixed hint behind a help trigger in its legend (H2 `hint` as a string). The panel
 * opens inside the legend, as H2's `legendNode` draws it (ADR-20261103-hlgd). */
function useStaticHint(question: ExposureQuestion, numbered: (text: string) => string) {
  const [open, setOpen] = useState(false);
  const hintId = `${question.fieldId}-hint`;
  if (question.hintLabel === undefined || question.helpText === undefined) {
    return { legend: numbered(question.legend), afterLegend: undefined, hasTrigger: false };
  }
  return {
    hasTrigger: true,
    legend: (
      <>
        {withTrigger(
          numbered(question.legend),
          <PortalPanel size="md" id={hintId} triggerLabel={question.hintLabel} expanded={open} onToggle={() => setOpen((value) => !value)} />,
        )}
        <PortalPanelBody id={hintId} expanded={open} spaced>
          {question.helpText}
        </PortalPanelBody>
      </>
    ),
    afterLegend: undefined,
  };
}

function YesNoDetailQuestion({
  question,
  answers,
  onChange,
  errors,
  disabled,
}: {
  readonly question: ExposureQuestion;
  readonly answers: ExposureAnswers;
  readonly onChange: (key: string, value: unknown) => void;
  readonly errors: readonly ExposureErrorSummaryItem[];
  readonly disabled: boolean;
}) {
  const answerKey = yesNoDetailAnswerKey(question.fieldId);
  const answer = readString(answers, answerKey);
  const answerError = errorFor(errors, answerKey);
  const detailKey = detailTextKey(question.fieldId);
  const detailError = errorFor(errors, detailKey);
  const numbered = useNumbered();
  const hint = useStaticHint(question, numbered);

  return (
    <Stack gap="sm">
      <RadioGroup
        id={answerKey}
        legend={hint.legend}
        value={answer === "yes" || answer === "no" ? answer : ""}
        onChange={(value) => {
          onChange(answerKey, value);
        }}
        options={YES_NO_OPTIONS}
        variant="plain"
        required
        error={answerError !== undefined}
        disabled={disabled}
        {...(question.helpText !== undefined && !hint.hasTrigger ? { help: question.helpText } : {})}
        {...(answerError !== undefined ? { errorText: answerError } : {})}
      />
      {answer === "yes" ? (
        <TextField
          id={detailKey}
          label={numbered(question.detailPrompt ?? "Provide details", "a")}
          value={readString(answers, detailKey)}
          onChange={(value) => {
            onChange(detailKey, value);
          }}
          multiline
          rows={2}
          autoComplete="off"
          error={detailError !== undefined}
          disabled={disabled}
          {...(detailError !== undefined ? { errorText: detailError } : {})}
        />
      ) : null}
    </Stack>
  );
}

function CurrencyQuestion({
  question,
  answers,
  onChange,
  errors,
  disabled,
}: {
  readonly question: ExposureQuestion;
  readonly answers: ExposureAnswers;
  readonly onChange: (key: string, value: unknown) => void;
  readonly errors: readonly ExposureErrorSummaryItem[];
  readonly disabled: boolean;
}) {
  const numbered = useNumbered();
  const figureError = errorFor(errors, question.fieldId);
  return (
    <TextField
      id={question.fieldId}
      label={numbered(question.legend)}
      figure
      value={readString(answers, question.fieldId)}
      onChange={(value) => {
        onChange(question.fieldId, value);
      }}
      autoComplete="off"
      disabled={disabled}
      error={figureError !== undefined}
      {...(figureError !== undefined ? { errorText: figureError } : {})}
      {...(question.currencyHelp !== undefined ? { help: question.currencyHelp } : {})}
    />
  );
}

/** The text an on-file figure's help panel shows: the figure, the default message when the
 * renewal holds none, or the failed-read message. */
function onFileFigureText(
  onFile: OnFileRenderContext | undefined,
  screenKey: string | undefined,
  attribute: string | undefined,
  itemsLabel: string,
): string {
  const status = onFile?.state.status ?? "loading";
  if (status === "failed") {
    return exposureContent.onFileReadFailedMessage;
  }
  const screen = findOnFileScreen(onFile, screenKey);
  const value =
    attribute === undefined
      ? undefined
      : (screen?.items ?? []).map((item) => onFileAttributeText(item.attributes[attribute])).find((text) => text !== undefined);
  return value !== undefined ? exposureContent.currentFigureText(value) : exposureContent.onFileNothingOnFile(itemsLabel);
}

/** A field's help: its trigger label and the text behind it, or `undefined` for none. */
interface FieldHelp {
  readonly triggerLabel: string;
  readonly text: string;
}

function fieldHelpFor(
  field: RepeatFieldSpec,
  item: RepeatItemAnswer | undefined,
  onFileSpec: SectionOnFileSpec | undefined,
  onFile: OnFileRenderContext | undefined,
): FieldHelp | undefined {
  if (field.explain !== undefined) {
    return field.explain;
  }
  if (field.onFileHelp !== undefined) {
    const { screenKey, attribute, itemsLabel } = field.onFileHelp;
    return {
      triggerLabel: exposureContent.currentFigureTriggerLabel(field.label),
      text: onFileFigureText(onFile, screenKey, attribute, itemsLabel),
    };
  }
  if (field.pickedHelp !== undefined && item !== undefined) {
    const pickerFieldId = onFileSpec?.picker?.fieldId;
    if (pickerFieldId === undefined || pickedOnFileItemFor(item, pickerFieldId) === undefined) {
      return undefined;
    }
    const attribute = onFileSpec?.picker?.prefill.find((prefill) => prefill.fieldId === field.id)?.attribute;
    const record = pickedRecord(item, onFileSpec, onFile);
    const value = attribute === undefined ? undefined : onFileAttributeText(record?.attributes[attribute]);
    return {
      triggerLabel: exposureContent.pickedFigureTriggerLabel(field.label),
      text:
        value !== undefined
          ? exposureContent.pickedFigureText(field.label, value)
          : exposureContent.onFileNothingOnFile(field.pickedHelp.itemsLabel),
    };
  }
  return undefined;
}

/** A text field, with H2's help trigger after its label when it has help. */
function HelpedTextField({
  id,
  label,
  value,
  onChange,
  error,
  disabled,
  help,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error: string | undefined;
  readonly disabled: boolean;
  readonly help: FieldHelp | undefined;
}) {
  const [open, setOpen] = useState(false);
  const helpId = `${id}-help-toggle`;
  return (
    <TextField
      id={id}
      label={label}
      value={value}
      onChange={onChange}
      autoComplete="off"
      fullWidth
      disabled={disabled}
      error={error !== undefined}
      {...(error !== undefined ? { errorText: error } : {})}
      {...(help !== undefined
        ? {
            labelAction: (
              <PortalPanel size="field" id={helpId} triggerLabel={help.triggerLabel} expanded={open} onToggle={() => setOpen((current) => !current)} />
            ),
            ...(open
              ? {
                  helpPanel: (
                    <PortalPanelBody id={helpId} expanded>
                      {help.text}
                    </PortalPanelBody>
                  ),
                }
              : {}),
          }
        : {})}
    />
  );
}

function CheckYearField({
  field,
  anchorId,
  item,
  onSetFields,
  errors,
  disabled,
}: {
  readonly field: RepeatFieldSpec;
  readonly anchorId: string;
  readonly item: RepeatItemAnswer;
  readonly onSetFields: (patch: Readonly<Record<string, string | undefined>>) => void;
  readonly errors: readonly ExposureErrorSummaryItem[];
  readonly disabled: boolean;
}) {
  const ticked = readCheckYear(item, field.id);
  const anchorPrefix = anchorId.slice(0, anchorId.length - field.id.length);
  const noneError = errorFor(errors, `${anchorId}-${field.options?.[0]?.value ?? ""}`);
  return (
    <fieldset style={{ border: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: `${sp.xs}px` }}>
      <legend style={{ ...LEGEND_STYLE, marginBottom: `${sp.xxs}px` }}>{field.label}</legend>
      {(field.options ?? []).map((option) => {
        const key = checkYearKey(field.id, option.value);
        const year = ticked.get(option.value);
        const yearError = errorFor(errors, `${anchorPrefix}${key}`);
        const boxError = errorFor(errors, `${anchorId}-${option.value}`);
        return (
          <div key={option.value} style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: `${sp.sm}px` }}>
            <div style={{ flex: "0 1 260px", minWidth: 0 }}>
              <Checkbox
                id={`${anchorId}-${option.value}`}
                label={option.label}
                checked={year !== undefined}
                onChange={(checked) => onSetFields({ [key]: checked ? (year ?? "") : undefined })}
                variant="plain"
                disabled={disabled}
                error={boxError !== undefined}
              />
            </div>
            {year !== undefined ? (
              <div style={{ flex: "0 0 240px" }}>
                <TextField
                  id={`${anchorPrefix}${key}`}
                  label={exposureContent.systemYearLabel}
                  value={year}
                  onChange={(value) => onSetFields({ [key]: value })}
                  autoComplete="off"
                  fullWidth
                  disabled={disabled}
                  error={yearError !== undefined}
                  {...(yearError !== undefined ? { errorText: yearError } : {})}
                />
              </div>
            ) : null}
          </div>
        );
      })}
      {noneError !== undefined ? (
        <span
          style={{
            fontSize: tokens.typography.variants.helper.size,
            fontWeight: tokens.typography.weights.bold,
            color: tokens.colour.status.error.text,
          }}
        >
          {noneError}
        </span>
      ) : null}
    </fieldset>
  );
}

interface FieldControlProps {
  readonly field: RepeatFieldSpec;
  readonly anchorId: string;
  /** The row the field belongs to; `fields` holds every answer the row's controls read. */
  readonly item: RepeatItemAnswer;
  readonly onSetFields: (patch: Readonly<Record<string, string | undefined>>) => void;
  readonly errors: readonly ExposureErrorSummaryItem[];
  readonly disabled: boolean;
  readonly onFileSpec?: SectionOnFileSpec;
  readonly onFile?: OnFileRenderContext;
  /** Picker only: the records another row or the sold source has already taken. */
  readonly excluded?: ReadonlySet<number>;
  readonly onPick?: (picked: OnFileScreenItem) => void;
  readonly onPickNew?: () => void;
  readonly claimFailureFocus?: () => boolean;
}

/** One field of a repeat row or an additional-details page, by H2 field type. */
function FieldControl({
  field,
  anchorId,
  item,
  onSetFields,
  errors,
  disabled,
  onFileSpec,
  onFile,
  excluded,
  onPick,
  onPickNew,
  claimFailureFocus,
}: Readonly<FieldControlProps>) {
  const error = errorFor(errors, anchorId);
  const storedValue = item.fields[field.id];
  const value = storedValue === undefined || storedValue === "" ? (field.defaultValue ?? "") : storedValue;
  const setValue = (next: string) => onSetFields({ [field.id]: next });
  const errorProps = error !== undefined ? { errorText: error } : {};

  switch (field.type) {
    case "picker": {
      const pickerSpec = onFileSpec?.picker ?? { fieldId: field.id, labelAttributes: [], prefill: [] };
      return (
        <OnFilePicker
          id={anchorId}
          label={field.label}
          pickerSpec={pickerSpec}
          emptyLabel={field.emptyLabel ?? exposureContent.defaultSelectEmptyLabel}
          noneLeftLabel={noneLeftLabelFor(field)}
          {...(field.newOption !== undefined ? { newOption: field.newOption } : {})}
          onFile={onFile}
          value={value}
          excluded={excluded ?? new Set()}
          disabled={disabled}
          claimFailureFocus={claimFailureFocus ?? (() => true)}
          {...(error !== undefined ? { error } : {})}
          onPick={(picked) => onPick?.(picked)}
          onPickNew={() => onPickNew?.()}
        />
      );
    }
    case "yesno":
    case "radio":
      return (
        <RadioGroup
          id={anchorId}
          legend={field.label}
          variant="plain"
          value={value}
          onChange={setValue}
          options={field.type === "yesno" ? YES_NO_OPTIONS : (field.options ?? [])}
          required
          error={error !== undefined}
          disabled={disabled}
          {...errorProps}
        />
      );
    case "select":
      return (
        <Select
          id={anchorId}
          label={field.label}
          value={value}
          onChange={setValue}
          options={field.options ?? []}
          emptyLabel={field.emptyLabel ?? exposureContent.defaultSelectEmptyLabel}
          error={error !== undefined}
          disabled={disabled}
          {...errorProps}
        />
      );
    case "date": {
      const parsed = value === "" ? null : dayjs(value);
      return (
        <DatePicker
          id={anchorId}
          label={field.label}
          help={exposureContent.dateFormatHelp}
          value={parsed !== null && parsed.isValid() ? parsed : null}
          onChange={(next) => setValue(next !== null && next.isValid() ? next.format("YYYY-MM-DD") : "")}
          {...(field.maxToday === true ? { maxDate: dayjs() } : {})}
          error={error !== undefined}
          disabled={disabled}
          {...errorProps}
        />
      );
    }
    case "checkYear":
      return <CheckYearField field={field} anchorId={anchorId} item={item} onSetFields={onSetFields} errors={errors} disabled={disabled} />;
    case "static":
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 17, fontWeight: tokens.typography.weights.bold, color: ink }}>{field.label}</span>
          <span style={{ fontSize: 17, color: ink }}>{value}</span>
        </div>
      );
    default:
      return (
        <HelpedTextField
          id={anchorId}
          label={field.label}
          value={value}
          onChange={setValue}
          error={error}
          disabled={disabled}
          help={fieldHelpFor(field, item, onFileSpec, onFile)}
        />
      );
  }
}

/** Drops every answer a row no longer shows (H2 `showIf`), so a changed answer never leaves a
 * stale value behind to be saved. */
function pruneHiddenFields(
  row: RepeatItemAnswer,
  fields: readonly RepeatFieldSpec[],
  pickerFieldId: string | undefined,
): RepeatItemAnswer {
  const hidden = fields.filter((field) => !isFieldOnScreen(field, row, fields, pickerFieldId));
  const staleKeys = Object.keys(row.fields).filter((key) => hidden.some((field) => isFieldAnswerKey(key, field.id)));
  const stalePicks = Object.keys(row.pickedOnFileItems ?? {}).filter((key) => hidden.some((field) => field.id === key));
  if (staleKeys.length === 0 && stalePicks.length === 0) {
    return row;
  }
  const kept = Object.fromEntries(Object.entries(row.fields).filter(([key]) => !staleKeys.includes(key)));
  const keptPicks = Object.fromEntries(Object.entries(row.pickedOnFileItems ?? {}).filter(([key]) => !stalePicks.includes(key)));
  return { ...row, fields: kept, ...(row.pickedOnFileItems !== undefined ? { pickedOnFileItems: keptPicks } : {}) };
}

function applyFieldPatch(row: RepeatItemAnswer, patch: Readonly<Record<string, string | undefined>>): RepeatItemAnswer {
  const fields: Record<string, string> = { ...row.fields };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) {
      delete fields[key];
    } else {
      fields[key] = value;
    }
  }
  return { ...row, fields };
}

/** A new row: H2 seeds each count with its default so an untouched count still saves a figure. */
function newRow(repeat: RepeatBlockSpec): RepeatItemAnswer {
  const fields = Object.fromEntries(
    repeat.fields.filter((field) => field.defaultValue !== undefined).map((field) => [field.id, field.defaultValue as string]),
  );
  return { key: createRepeatItemKey(), outcome: "", fields };
}

/** Records an on-file pick on `row`, prefilling the fields its picker declares. */
function withPick(row: RepeatItemAnswer, pickerFieldId: string, onFileSpec: SectionOnFileSpec, picked: OnFileScreenItem): RepeatItemAnswer {
  const pickerSpec = onFileSpec.picker;
  if (picked.epicRecordId === null || pickerSpec?.screenKey === undefined) {
    return row;
  }
  let next = withPickedOnFileItem(row, pickerFieldId, { screenKey: pickerSpec.screenKey, epicRecordId: picked.epicRecordId });
  for (const prefill of pickerSpec.prefill) {
    next = applyFieldPatch(next, { [prefill.fieldId]: onFileAttributeText(picked.attributes[prefill.attribute]) ?? "" });
  }
  return next;
}

function withoutPick(row: RepeatItemAnswer, pickerFieldId: string, value: string): RepeatItemAnswer {
  const picks = { ...row.pickedOnFileItems };
  delete picks[pickerFieldId];
  return { ...applyFieldPatch(row, { [pickerFieldId]: value }), pickedOnFileItems: picks };
}

function RepeatRow({
  question,
  repeat,
  row,
  rowNumber,
  title,
  canRemove,
  onRemove,
  onUpdate,
  fields,
  errors,
  disabled,
  onFileSpec,
  onFile,
  excludedFor,
  claimFailureFocus,
  anchorPrefix,
}: {
  readonly question: ExposureQuestion;
  readonly repeat: RepeatBlockSpec;
  readonly row: RepeatItemAnswer;
  readonly rowNumber: number;
  readonly title: string | undefined;
  readonly canRemove: boolean;
  readonly onRemove: () => void;
  readonly onUpdate: (update: (row: RepeatItemAnswer) => RepeatItemAnswer) => void;
  readonly fields: readonly RepeatFieldSpec[];
  readonly errors: readonly ExposureErrorSummaryItem[];
  readonly disabled: boolean;
  readonly onFileSpec?: SectionOnFileSpec;
  readonly onFile?: OnFileRenderContext;
  readonly excludedFor?: (field: RepeatFieldSpec) => ReadonlySet<number>;
  readonly claimFailureFocus: () => boolean;
  readonly anchorPrefix: string;
}) {
  const outcomeAnchor = `${anchorPrefix}outcome`;
  const outcomeError = errorFor(errors, outcomeAnchor);
  const pickerFieldId = onFileSpec?.picker?.fieldId;
  const visibleFields = fields.filter((field) => isFieldOnScreen(field, row, fields, pickerFieldId));

  return (
    <div style={ROW_STYLE} data-testid={`${question.fieldId}-item-${rowNumber}`}>
      <div style={ROW_HEAD_STYLE}>
        {title !== undefined ? (
          <span style={ROW_TITLE_STYLE} data-testid={`${anchorPrefix}title`}>
            {title}
          </span>
        ) : null}
        {canRemove ? (
          <Button emphasis="quiet" onClick={onRemove} disabled={disabled} testId={`${anchorPrefix}remove`}>
            {exposureContent.removeLabel}
          </Button>
        ) : null}
      </div>
      <div style={FIELDS_ROW_STYLE}>
        {repeat.outcomeOptions !== undefined && fields === repeat.fields ? (
          <div style={fieldWrapStyle(false)}>
            <RadioGroup
              id={outcomeAnchor}
              legend={repeat.outcomeLegend ?? ""}
              variant="plain"
              value={row.outcome}
              onChange={(value) => onUpdate((current) => ({ ...current, outcome: value }))}
              options={repeat.outcomeOptions}
              required
              error={outcomeError !== undefined}
              disabled={disabled}
              {...(outcomeError !== undefined ? { errorText: outcomeError } : {})}
            />
          </div>
        ) : null}
        {visibleFields.map((field) => (
          <div key={field.id} style={fieldWrapStyle(field.wide)}>
            <FieldControl
              field={field}
              anchorId={`${anchorPrefix}${field.id}`}
              item={row}
              onSetFields={(patch) => onUpdate((current) => applyFieldPatch(current, patch))}
              errors={errors}
              disabled={disabled}
              {...(onFileSpec !== undefined ? { onFileSpec } : {})}
              {...(onFile !== undefined ? { onFile } : {})}
              {...(field.type === "picker"
                ? {
                    excluded: excludedFor?.(field) ?? new Set<number>(),
                    onPick: (picked: OnFileScreenItem) =>
                      onUpdate((current) => (onFileSpec === undefined ? current : withPick(current, field.id, onFileSpec, picked))),
                    onPickNew: () => onUpdate((current) => withoutPick(current, field.id, PICKER_NEW_OPTION_VALUE)),
                    claimFailureFocus,
                  }
                : {})}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

interface DataQuestionProps {
  readonly sectionId: SectionId;
  readonly question: ExposureQuestion;
  readonly answers: ExposureAnswers;
  readonly onChange: (key: string, value: unknown) => void;
  readonly errors: readonly ExposureErrorSummaryItem[];
  readonly disabled: boolean;
  readonly onFile?: OnFileRenderContext;
  readonly panelExpanded: boolean;
  readonly onTogglePanel: () => void;
  readonly claimFailureFocus: () => boolean;
}

/** The question's legend with its help trigger, and the help panel shown after the legend. An
 * on-file question's panel lists what the renewal holds; a question with fixed guidance shows it. */
function useQuestionHelp(props: DataQuestionProps, pickerMounted: boolean) {
  const { sectionId, question, answers, onFile, panelExpanded, onTogglePanel, claimFailureFocus } = props;
  const numbered = useNumbered();
  const staticHint = useStaticHint(question, numbered);
  const onFileSpec = getOnFileFieldSpec(sectionId, question.fieldId);
  if (onFileSpec === undefined || question.hintLabel !== undefined) {
    return { onFileSpec, legend: staticHint.legend, afterLegend: staticHint.afterLegend, describedBy: undefined };
  }
  // The panel opens inside the legend, as H2's `legendNode` draws it (ADR-20261103-hlgd), so its
  // content joins the group's accessible name.
  const panelId = `${question.fieldId}-on-file`;
  return {
    onFileSpec,
    legend: (
      <>
        {withTrigger(
          numbered(question.legend),
          <OnFilePanelTrigger id={panelId} spec={onFileSpec} expanded={panelExpanded} onToggle={onTogglePanel} />,
        )}
        <OnFilePanelBody
          id={panelId}
          spec={onFileSpec}
          onFile={onFile}
          expanded={panelExpanded}
          flagItems={soldSourceRows(onFileSpec, answers)}
          retryOwnedElsewhere={pickerMounted}
          pickerDeclared={onFileSpec.picker !== undefined}
          claimFailureFocus={claimFailureFocus}
        />
      </>
    ),
    afterLegend: undefined,
    describedBy: undefined,
  };
}

function YesNoRepeatQuestion(props: DataQuestionProps) {
  const { question, answers, onChange, errors, disabled, onFile, claimFailureFocus } = props;
  const numbered = useNumbered();
  const repeat = question.repeat;
  const answerKey = repeatAnswerKey(question.fieldId);
  const answer = readString(answers, answerKey);
  const rows = readRepeatItems(answers, question.fieldId);
  const pickerMounted = answer === "yes" && rows.length > 0 && (repeat?.fields.some((field) => field.type === "picker") ?? false);
  const help = useQuestionHelp(props, pickerMounted);
  if (repeat === undefined) {
    return null;
  }
  const { onFileSpec } = help;
  const answerError = errorFor(errors, answerKey);
  const pickerFieldId = onFileSpec?.picker?.fieldId;
  const soldRows = soldSourceRows(onFileSpec, answers);

  function writeRows(nextRows: readonly RepeatItemAnswer[]) {
    onChange(
      repeatItemsKey(question.fieldId),
      nextRows.map((row) => pruneHiddenFields(row, repeat?.fields ?? [], pickerFieldId)),
    );
  }

  function excludedFor(row: RepeatItemAnswer): (field: RepeatFieldSpec) => ReadonlySet<number> {
    return (field) => {
      const screenKey = onFileSpec?.picker?.screenKey;
      const excluded = new Set(excludedEpicRecordIds(screenKey, field.id, rows, row.key));
      const soldSource = onFileSpec?.soldSource;
      if (field.soldAware === true && soldSource !== undefined) {
        for (const id of soldEpicRecordIds(screenKey, soldSource.pickerFieldId, soldRows, soldSource.soldOutcome)) {
          excluded.add(id);
        }
      }
      return excluded;
    };
  }

  const single = repeat.single === true;
  return (
    <Stack gap="sm">
      <RadioGroup
        id={answerKey}
        legend={help.legend}
        afterLegend={help.afterLegend}
        {...(help.describedBy !== undefined ? { "aria-describedby": help.describedBy } : {})}
        variant="plain"
        value={answer === "yes" || answer === "no" ? answer : ""}
        onChange={(value) => {
          onChange(answerKey, value);
          // H2: "yes" opens one empty set of fields, so the customer never lands on an empty block.
          if (value === "yes" && rows.length === 0) {
            writeRows([newRow(repeat)]);
          }
        }}
        options={YES_NO_OPTIONS}
        required
        error={answerError !== undefined}
        disabled={disabled}
        {...(answerError !== undefined ? { errorText: answerError } : {})}
      />
      {answer === "yes" ? (
        <fieldset style={FIELDSET_STYLE}>
          <legend style={repeat.legendHidden === true ? LEGEND_HIDDEN_STYLE : LEGEND_STYLE}>{numbered(repeat.legend, "a")}</legend>
          {rows.map((row, index) => (
            <RepeatRow
              key={row.key}
              question={question}
              repeat={repeat}
              row={row}
              rowNumber={index + 1}
              title={single ? undefined : exposureContent.rowTitle(repeat.itemLabel, index + 1, repeat.noTitle === true)}
              canRemove={rows.length > 1 && !single}
              onRemove={() => writeRows(rows.filter((candidate) => candidate.key !== row.key))}
              onUpdate={(update) => writeRows(rows.map((candidate) => (candidate.key === row.key ? update(candidate) : candidate)))}
              fields={repeat.fields}
              errors={errors}
              disabled={disabled}
              {...(onFileSpec !== undefined ? { onFileSpec } : {})}
              {...(onFile !== undefined ? { onFile } : {})}
              excludedFor={excludedFor(row)}
              claimFailureFocus={claimFailureFocus}
              anchorPrefix={`${question.fieldId}-${row.key}-`}
            />
          ))}
          <div>
            {single ? null : (
              <Button
                emphasis="secondary"
                onClick={() => writeRows([...rows, newRow(repeat)])}
                disabled={disabled}
                testId={`${question.fieldId}-add-another`}
              >
                {repeat.addLabel}
              </Button>
            )}
          </div>
        </fieldset>
      ) : null}
    </Stack>
  );
}

/** The subjects of insurance a picked building holds on file, plus H2's "Other". */
function limitsSubjectOptions(
  question: ExposureQuestion,
  onFileSpec: SectionOnFileSpec | undefined,
  onFile: OnFileRenderContext | undefined,
  block: LimitsBlockAnswer,
): readonly (FieldOption & { readonly amount?: string })[] {
  const limits = question.limits;
  const subjectsSpec = onFileSpec?.limitsSubjects;
  const building = findOnFileScreen(onFile, onFileSpec?.picker?.screenKey)?.items.find(
    (item) => String(item.epicRecordId) === block.property,
  );
  const match = subjectsSpec === undefined ? undefined : onFileAttributeText(building?.attributes[subjectsSpec.matchAttribute]);
  const onFileSubjects =
    subjectsSpec === undefined || match === undefined
      ? []
      : (findOnFileScreen(onFile, subjectsSpec.screenKey)?.items ?? [])
          .filter((item) => item.epicRecordId !== null && onFileAttributeText(item.attributes[subjectsSpec.matchAttribute]) === match)
          .map((item) => {
            const amount = onFileAttributeText(item.attributes[subjectsSpec.amountAttribute]);
            return {
              value: String(item.epicRecordId),
              label: onFileAttributeText(item.attributes[subjectsSpec.labelAttribute]) ?? exposureContent.onFileItemFallbackLabel(item.rowIndex + 1),
              ...(amount !== undefined ? { amount } : {}),
            };
          });
  return limits === undefined ? onFileSubjects : [...onFileSubjects, limits.otherSubject];
}

/** Answer key for a subject's updated limit; Epic holds no valuation basis to key it by. */
const LIMIT_VALUE_KEY = "limit";

function LimitValueField({
  id,
  subjectLabel,
  amount,
  itemsLabel,
  value,
  onChange,
  error,
  disabled,
}: {
  readonly id: string;
  readonly subjectLabel: string;
  readonly amount: string | undefined;
  readonly itemsLabel: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error: string | undefined;
  readonly disabled: boolean;
}) {
  return (
    <HelpedTextField
      id={id}
      label={exposureContent.limitsValueLabel}
      value={value}
      onChange={onChange}
      error={error}
      disabled={disabled}
      help={{
        triggerLabel: exposureContent.limitsCurrentTriggerLabel(subjectLabel),
        text: amount !== undefined ? exposureContent.limitsCurrentText(amount) : exposureContent.onFileNothingOnFile(itemsLabel),
      }}
    />
  );
}

/** H2 commercial property question 4: pick a property, add each subject of insurance it holds,
 * and enter the updated limit. */
function LimitsQuestion(props: DataQuestionProps) {
  const { sectionId, question, answers, onChange, errors, disabled, onFile, claimFailureFocus } = props;
  const numbered = useNumbered();
  const help = useQuestionHelp(props, false);
  const [pending, setPending] = useState<Readonly<Record<string, string>>>({});
  const [addErrors, setAddErrors] = useState<Readonly<Record<string, boolean>>>({});
  const answerKey = repeatAnswerKey(question.fieldId);
  const answer = readString(answers, answerKey);
  const answerError = errorFor(errors, answerKey);
  const blocks = readLimitsBlocks(answers, question.fieldId);
  const onFileSpec = getOnFileFieldSpec(sectionId, question.fieldId);
  const pickerSpec = onFileSpec?.picker;
  const buildings = findOnFileScreen(onFile, pickerSpec?.screenKey)?.items ?? [];
  const soldSource = onFileSpec?.soldSource;
  const sold =
    soldSource === undefined
      ? new Set<number>()
      : soldEpicRecordIds(pickerSpec?.screenKey, soldSource.pickerFieldId, soldSourceRows(onFileSpec, answers), soldSource.soldOutcome);
  const itemsLabel = onFileSpec?.itemsLabel ?? "limits";

  const writeBlocks = (next: readonly LimitsBlockAnswer[]) => onChange(question.fieldId, next);
  const updateBlock = (key: string, update: (block: LimitsBlockAnswer) => LimitsBlockAnswer) =>
    writeBlocks(blocks.map((block) => (block.key === key ? update(block) : block)));
  const available = buildings.filter((item) => item.epicRecordId !== null && !sold.has(item.epicRecordId));
  const canAddProperty = blocks.length < available.length;

  return (
    <Stack gap="sm">
      <RadioGroup
        id={answerKey}
        legend={help.legend}
        afterLegend={help.afterLegend}
        variant="plain"
        value={answer === "yes" || answer === "no" ? answer : ""}
        onChange={(value) => {
          onChange(answerKey, value);
          if (value === "yes" && blocks.length === 0) {
            writeBlocks([{ key: createRepeatItemKey(), property: "", subjects: [] }]);
          }
        }}
        options={YES_NO_OPTIONS}
        required
        error={answerError !== undefined}
        disabled={disabled}
        {...(answerError !== undefined ? { errorText: answerError } : {})}
      />
      {answer === "yes" && question.limits !== undefined ? (
        <fieldset style={FIELDSET_STYLE}>
          <legend style={LEGEND_STYLE}>{numbered(question.limits.legend, "a")}</legend>
          {blocks.map((block, index) => {
            const anchor = `${question.fieldId}-${block.key}`;
            const used = new Set(blocks.filter((other) => other.key !== block.key).map((other) => other.property));
            const subjects = limitsSubjectOptions(question, onFileSpec, onFile, block);
            const remaining = subjects.filter((subject) => !block.subjects.some((added) => added.subject === subject.value));
            const subjectError = errorFor(errors, `${anchor}-subject`) ?? (addErrors[block.key] === true ? exposureContent.limitsSubjectNotChosen : undefined);
            return (
              <div
                key={block.key}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: `${sp.sm}px`,
                  minWidth: 0,
                  paddingBottom: `${sp.sm}px`,
                  borderBottom: index < blocks.length - 1 ? `1px solid ${tokens.colour.neutral.borderSubtle}` : "none",
                }}
              >
                <Stack direction="actions" gap="sm" align="start">
                  <div style={{ flex: "1 1 auto", minWidth: 0, maxWidth: "100%", width: "100%" }}>
                    <OnFilePicker
                      id={`${anchor}-property`}
                      label={exposureContent.limitsPropertyLabel}
                      pickerSpec={pickerSpec ?? { fieldId: "property", labelAttributes: [], prefill: [] }}
                      emptyLabel={exposureContent.limitsPropertyEmptyLabel}
                      noneLeftLabel={exposureContent.limitsPropertyNoneLeftLabel}
                      onFile={onFile}
                      value={block.property}
                      excluded={new Set([...sold, ...[...used].filter((id) => id !== "").map(Number)])}
                      disabled={disabled}
                      claimFailureFocus={claimFailureFocus}
                      {...(errorFor(errors, `${anchor}-property`) !== undefined ? { error: errorFor(errors, `${anchor}-property`) as string } : {})}
                      onPick={(picked) =>
                        updateBlock(block.key, (current) => ({
                          key: current.key,
                          property: String(picked.epicRecordId),
                          ...(pickerSpec?.screenKey !== undefined && picked.epicRecordId !== null
                            ? { pickedOnFileItem: { screenKey: pickerSpec.screenKey, epicRecordId: picked.epicRecordId } }
                            : {}),
                          // Subjects belong to one property; switching clears what this block held.
                          subjects: [],
                        }))
                      }
                    />
                  </div>
                  {blocks.length > 1 ? (
                    <div style={{ flex: "0 0 auto", paddingTop: 30, whiteSpace: "nowrap" }}>
                      <Button emphasis="quiet" onClick={() => writeBlocks(blocks.filter((other) => other.key !== block.key))} disabled={disabled}>
                        {exposureContent.limitsRemovePropertyLabel}
                      </Button>
                    </div>
                  ) : null}
                </Stack>
                {block.property !== "" ? (
                  <>
                    {block.subjects.map((added) => {
                      const option = subjects.find((subject) => subject.value === added.subject);
                      return (
                        <div key={added.subject} style={ROW_STYLE}>
                          <div style={ROW_HEAD_STYLE}>
                            <span style={ROW_TITLE_STYLE}>{added.label}</span>
                            <Button
                              emphasis="quiet"
                              disabled={disabled}
                              onClick={() =>
                                updateBlock(block.key, (current) => ({
                                  ...current,
                                  subjects: current.subjects.filter((other) => other.subject !== added.subject),
                                }))
                              }
                            >
                              {exposureContent.removeLabel}
                            </Button>
                          </div>
                          <div style={{ ...FIELDS_ROW_STYLE, maxWidth: 360 }}>
                            <LimitValueField
                              id={`${anchor}-${added.subject}-value`}
                              subjectLabel={added.label}
                              amount={option?.amount}
                              itemsLabel={itemsLabel}
                              value={added.values[LIMIT_VALUE_KEY] ?? ""}
                              error={errorFor(errors, `${anchor}-${added.subject}-value`)}
                              disabled={disabled}
                              onChange={(value) =>
                                updateBlock(block.key, (current) => ({
                                  ...current,
                                  subjects: current.subjects.map((other) =>
                                    other.subject === added.subject ? { ...other, values: { ...other.values, [LIMIT_VALUE_KEY]: value } } : other,
                                  ),
                                }))
                              }
                            />
                          </div>
                        </div>
                      );
                    })}
                    {remaining.length === 0 ? <span style={MUTED_NOTE_STYLE}>{exposureContent.limitsAllSubjectsAdded}</span> : null}
                    {remaining.length > 0 ? (
                      <Stack direction="actions" gap="sm" align="start">
                        <div style={{ flex: "1 1 auto", minWidth: 0, maxWidth: "100%", width: "100%" }}>
                          <Select
                            id={`${anchor}-subject`}
                            label={exposureContent.limitsSubjectLabel}
                            value={pending[block.key] ?? ""}
                            onChange={(value) => setPending((current) => ({ ...current, [block.key]: value }))}
                            options={remaining.map(({ value, label }) => ({ value, label }))}
                            error={subjectError !== undefined}
                            disabled={disabled}
                            {...(subjectError !== undefined ? { errorText: subjectError } : {})}
                          />
                        </div>
                        <div style={{ flex: "0 0 auto", paddingTop: 30 }}>
                          <Button
                            emphasis="secondary"
                            disabled={disabled}
                            onClick={() => {
                              const chosen = remaining.find((subject) => subject.value === pending[block.key]);
                              if (chosen === undefined) {
                                setAddErrors((current) => ({ ...current, [block.key]: true }));
                                return;
                              }
                              setAddErrors((current) => ({ ...current, [block.key]: false }));
                              setPending((current) => ({ ...current, [block.key]: "" }));
                              updateBlock(block.key, (current) => ({
                                ...current,
                                subjects: [...current.subjects, { subject: chosen.value, label: chosen.label, values: {} }],
                              }));
                            }}
                          >
                            {exposureContent.limitsAddSubjectLabel}
                          </Button>
                        </div>
                      </Stack>
                    ) : null}
                  </>
                ) : null}
              </div>
            );
          })}
          {canAddProperty ? (
            <div>
              <Button
                emphasis="secondary"
                disabled={disabled}
                onClick={() => writeBlocks([...blocks, { key: createRepeatItemKey(), property: "", subjects: [] }])}
              >
                {exposureContent.limitsAddPropertyLabel}
              </Button>
            </div>
          ) : null}
        </fieldset>
      ) : null}
    </Stack>
  );
}

// H2 `checkGrid`: the minimum width a checklist item is given before `auto-fill` wraps it.
const CHECKLIST_ITEM_MIN_WIDTH_PX = 280;

/** One checklist item (H2 `boxes`): the checkbox, its definition behind a help trigger, and for
 * "Other" the free-text field ticking it reveals. */
function ChecklistItemRow({
  questionFieldId,
  item,
  checked,
  onToggle,
  open,
  onToggleDefinition,
  disabled,
  otherTextKey,
  otherValue,
  onOtherChange,
}: {
  readonly questionFieldId: string;
  readonly item: ChecklistItemSpec;
  readonly checked: boolean;
  readonly onToggle: (checked: boolean) => void;
  readonly open: boolean;
  readonly onToggleDefinition: () => void;
  readonly disabled: boolean;
  readonly otherTextKey?: string;
  readonly otherValue?: string;
  readonly onOtherChange?: (value: string) => void;
}) {
  const definitionId = `${questionFieldId}-${item.id}-definition`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: `${sp.xxs}px` }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `minmax(0, 1fr) ${tokens.interactionState.targetSize.standard}px`,
          alignItems: "start",
          gap: 2,
        }}
      >
        <Checkbox id={`${questionFieldId}-${item.id}`} label={item.label} checked={checked} onChange={onToggle} variant="plain" disabled={disabled} />
        {item.helpText !== undefined ? (
          <PortalPanel
            size="sm"
            id={definitionId}
            triggerLabel={exposureContent.checklistDefinitionTriggerLabel(item.label)}
            expanded={open}
            onToggle={onToggleDefinition}
          />
        ) : null}
      </div>
      {item.helpText !== undefined ? (
        <PortalPanelBody id={definitionId} expanded={open}>
          {item.helpText}
        </PortalPanelBody>
      ) : null}
      {otherTextKey !== undefined && checked ? (
        <TextField
          id={otherTextKey}
          label={exposureContent.otherCoverageLabel}
          value={otherValue ?? ""}
          onChange={(value) => {
            onOtherChange?.(value);
          }}
          multiline
          rows={2}
          autoComplete="off"
          disabled={disabled}
        />
      ) : null}
    </div>
  );
}

function ChecklistQuestion({
  question,
  answers,
  onChange,
  disabled,
}: {
  readonly question: ExposureQuestion;
  readonly answers: ExposureAnswers;
  readonly onChange: (key: string, value: unknown) => void;
  readonly disabled: boolean;
}) {
  const checklist = question.checklist;
  // One open/closed flag per item's definition panel (closed by default); never written to answers.
  const [openDefinitions, setOpenDefinitions] = useState<Readonly<Record<string, boolean>>>({});
  const numbered = useNumbered();
  if (checklist === undefined) {
    return null;
  }
  const tickedKey = checklistTickedKey(question.fieldId);
  const ticked = readStringArray(answers, tickedKey);
  const otherTextKey = checklistOtherTextKey(question.fieldId);

  return (
    // Native `fieldset`/`legend` (WCAG 2.2 AA SC 1.3.1).
    <fieldset style={FIELDSET_STYLE}>
      <legend style={LEGEND_STYLE}>{numbered(checklist.legend)}</legend>
      <div
        style={{
          display: "grid",
          gap: `${sp.xs}px`,
          gridTemplateColumns: `repeat(auto-fill, minmax(${CHECKLIST_ITEM_MIN_WIDTH_PX}px, 1fr))`,
        }}
      >
        {checklist.items.map((item) => (
          <ChecklistItemRow
            key={item.id}
            questionFieldId={question.fieldId}
            item={item}
            checked={ticked.includes(item.id)}
            onToggle={(checked) => {
              const next = checked ? [...ticked, item.id] : ticked.filter((id) => id !== item.id);
              onChange(tickedKey, next);
            }}
            open={openDefinitions[item.id] ?? false}
            onToggleDefinition={() => {
              setOpenDefinitions((current) => ({ ...current, [item.id]: !(current[item.id] ?? false) }));
            }}
            disabled={disabled}
            {...(checklist.otherItemId === item.id
              ? {
                  otherTextKey,
                  otherValue: readString(answers, otherTextKey),
                  onOtherChange: (value: string) => {
                    onChange(otherTextKey, value);
                  },
                }
              : {})}
          />
        ))}
      </div>
      {checklist.closingNote !== undefined ? <span style={MUTED_NOTE_STYLE}>{checklist.closingNote}</span> : null}
    </fieldset>
  );
}

/**
 * One additional-details page (H2 `repeatDetail`): the parent row's further fields under the
 * legend "1. Provide details", titled for the item. Answers are this page's own; its show rules
 * read the parent row's change type too.
 */
export function AdditionalDetailsPage({
  parent,
  title,
  answers,
  onChange,
  errors,
  disabled = false,
}: Readonly<{
  readonly parent: DetailsPageRow;
  readonly title: string;
  readonly answers: ExposureAnswers;
  readonly onChange: (fieldId: string, value: unknown) => void;
  readonly errors: readonly ExposureErrorSummaryItem[];
  readonly disabled?: boolean;
}>) {
  const repeat = parent.question.repeat;
  const page = repeat?.additionalDetailsPage;
  if (repeat === undefined || page === undefined) {
    return null;
  }
  const row = detailsShowRuleRow(parent, answers);
  return (
    <Stack gap="md">
      {errors.length > 0 ? (
        <ErrorSummary items={errors.map((error) => ({ fieldId: error.anchorId, text: error.text }))} id="additional-details-error-summary" />
      ) : null}
      <QuestionRow>
        <Stack gap="sm">
          <fieldset style={FIELDSET_STYLE}>
            <legend style={LEGEND_STYLE}>{`1. ${repeat.legend}`}</legend>
            <RepeatRow
              question={parent.question}
              repeat={repeat}
              row={row}
              rowNumber={1}
              title={title}
              canRemove={false}
              onRemove={() => {}}
              onUpdate={(update) => {
                const next = pruneHiddenFields(update(row), page.fields, undefined);
                for (const field of page.fields) {
                  for (const key of new Set([...Object.keys(row.fields), ...Object.keys(next.fields)])) {
                    if (isFieldAnswerKey(key, field.id) && next.fields[key] !== row.fields[key]) {
                      onChange(key, next.fields[key] ?? "");
                    }
                  }
                }
              }}
              fields={page.fields}
              errors={errors}
              disabled={disabled}
              claimFailureFocus={() => true}
              anchorPrefix=""
            />
          </fieldset>
        </Stack>
      </QuestionRow>
    </Stack>
  );
}

/**
 * Renders every question in one exposure section's schema (AC-15). Purely presentational and
 * controlled: `answers` is the caller's editable state for this one section instance, `onChange`
 * writes a single key, and this component persists nothing itself.
 */
export function ExposureQuestionsEngine({
  schema,
  answers,
  onChange,
  errors,
  disabled = false,
  onFile,
}: Readonly<ExposureQuestionsEngineProps>) {
  // One expand/collapse flag per data-on-file question, keyed by its fieldId; never saved.
  const [expandedPanels, setExpandedPanels] = useState<Readonly<Record<string, boolean>>>({});
  const togglePanel = (fieldId: string) => {
    setExpandedPanels((current) => ({ ...current, [fieldId]: !(current[fieldId] ?? false) }));
  };
  // AC-4/SC 2.4.3: a read that becomes Failed opens every help-only panel (no picker owns the
  // retry control) once, so its retry control is reachable. Derived while rendering, not in an
  // effect: `undefined` as the starting value makes a read already failed on mount count too.
  const onFileStatus = onFile?.state.status;
  const [seenOnFileStatus, setSeenOnFileStatus] = useState<string | undefined>(undefined);
  if (seenOnFileStatus !== onFileStatus) {
    setSeenOnFileStatus(onFileStatus);
    if (onFileStatus === "failed") {
      const failedOpen = schema.questions.filter((question) => {
        const spec = getOnFileFieldSpec(schema.sectionId, question.fieldId);
        return spec !== undefined && spec.picker === undefined;
      });
      if (failedOpen.length > 0) {
        setExpandedPanels((current) => ({
          ...current,
          ...Object.fromEntries(failedOpen.map((question) => [question.fieldId, true])),
        }));
      }
    }
  }
  // Shared across every data-on-file question in this section instance (`useFailureFocusClaim`).
  const claimFailureFocus = useFailureFocusClaim(onFile?.state.status);
  const onFileProp = onFile !== undefined ? { onFile } : {};

  // H2 4&5: the error summary and each question are the section body's blocks, 16px apart.
  return (
    <Stack gap="md">
      {errors.length > 0 ? (
        <ErrorSummary items={errors.map((error) => ({ fieldId: error.anchorId, text: error.text }))} id={`${schema.sectionId}-error-summary`} />
      ) : null}
      {schema.questions.map((question, index) => {
        const dataProps: DataQuestionProps = {
          sectionId: schema.sectionId,
          question,
          answers,
          onChange,
          errors,
          disabled,
          ...onFileProp,
          panelExpanded: expandedPanels[question.fieldId] ?? false,
          onTogglePanel: () => togglePanel(question.fieldId),
          claimFailureFocus,
        };
        return (
          <QuestionNumberContext.Provider key={question.fieldId} value={question.number ?? index + 1}>
            <QuestionRow>
              {question.type === "yesno-detail" ? (
                <YesNoDetailQuestion question={question} answers={answers} onChange={onChange} errors={errors} disabled={disabled} />
              ) : null}
              {question.type === "yesno-repeat" ? <YesNoRepeatQuestion {...dataProps} /> : null}
              {question.type === "limits" ? <LimitsQuestion {...dataProps} /> : null}
              {question.type === "checkbox-list" ? (
                <ChecklistQuestion question={question} answers={answers} onChange={onChange} disabled={disabled} />
              ) : null}
              {question.type === "currency" ? (
                <CurrencyQuestion question={question} answers={answers} onChange={onChange} errors={errors} disabled={disabled} />
              ) : null}
            </QuestionRow>
          </QuestionNumberContext.Provider>
        );
      })}
    </Stack>
  );
}
